import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MapGL, { Source, Layer, Popup, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import Button from "@/components/ui/Button.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { useTheme } from "@/contexts/ThemeContext.jsx";
import useGeolocation from "@/hooks/useGeolocation.js";
import regionalHealthService from "@/services/regionalHealthService.js";
import supabase from "@/services/supabaseClient";
import { readDoctorConsultancies } from "@/lib/localDoctorStorage";
import { buildSouthAfricaProvinceChoropleth } from "@/utils/regionalHealthSouthAfrica.js";
import { getAfriMedMapStyle } from "@/constants/mapStyles.js";
import {
  REGIONAL_HEALTH_COUNTRY_VIEWS as COUNTRY_VIEW,
  REGIONAL_HEALTH_SADC_BOUNDS as SADC_BOUNDS,
  REGIONAL_HEALTH_SADC_DEFAULT_VIEW as SADC_DEFAULT_VIEW,
  RegionalHealthMapLegendRows,
  RegionalHealthMapScopeControls,
  resolveRegionalHealthCountryLabel,
} from "@/pages/regional-health/RegionalHealthMapPresentation.jsx";

const ICON_CORONAVIRUS = "/icons/doctor/home_content_media/coronavirus.svg";

const STATUS_META = {
  good: { label: "Good", color: "#5D7092", score: 1 },
  acceptable: { label: "Acceptable", color: "#8E6CEF", score: 2 },
  caution: { label: "Caution", color: "#F0B100", score: 3 },
  critical: { label: "Critical", color: "#F7543A", score: 4 },
};

const COUNTRY_ALIASES = {
  Angola: ["angola"],
  Botswana: ["botswana", "bwa", "bw"],
  Comoros: ["comoros"],
  "DR Congo": [
    "dr congo",
    "drc",
    "congo kinshasa",
    "democratic republic of the congo",
  ],
  Eswatini: ["eswatini", "swaziland", "sz"],
  Lesotho: ["lesotho", "ls"],
  Madagascar: ["madagascar", "mg"],
  Malawi: ["malawi", "mw"],
  Mauritius: ["mauritius", "mu"],
  Mozambique: ["mozambique", "mz"],
  Namibia: ["namibia", "na"],
  Seychelles: ["seychelles", "sc"],
  "South Africa": ["south africa", "rsa", "za"],
  Tanzania: ["tanzania", "tz"],
  Zambia: ["zambia", "zm"],
  Zimbabwe: ["zimbabwe", "zw"],
};

const MOCK_SIGNALS = [
  {
    id: "bw-gaborone-rsv",
    country: "Botswana",
    region: "Gaborone",
    province: "Gaborone",
    disease: "Coronavirus",
    status: "critical",
    patients: 1780,
    updated: "Today",
    lat: -24.6282,
    lng: 25.9231,
    source: "mock",
  },
  {
    id: "bw-francistown-influenza",
    country: "Botswana",
    region: "Francistown",
    province: "Francistown",
    disease: "Influenza",
    status: "acceptable",
    patients: 248,
    updated: "Today",
    lat: -21.17,
    lng: 27.5078,
    source: "mock",
  },
  {
    id: "bw-maun-malaria",
    country: "Botswana",
    region: "Maun",
    province: "Maun",
    disease: "Malaria",
    status: "caution",
    patients: 420,
    updated: "Yesterday",
    lat: -19.9833,
    lng: 23.4167,
    source: "mock",
  },
  {
    id: "za-western-cape-rsv",
    country: "South Africa",
    region: "Western Cape",
    province: "Western Cape",
    disease: "RSV",
    status: "caution",
    patients: 602,
    updated: "Today",
    lat: -33.9249,
    lng: 18.4241,
    source: "mock",
  },
  {
    id: "za-gauteng-covid",
    country: "South Africa",
    region: "Gauteng",
    province: "Gauteng",
    disease: "Coronavirus",
    status: "acceptable",
    patients: 380,
    updated: "Today",
    lat: -26.2041,
    lng: 28.0473,
    source: "mock",
  },
  {
    id: "zw-harare-cholera",
    country: "Zimbabwe",
    region: "Harare",
    province: "Harare",
    disease: "Cholera",
    status: "critical",
    patients: 894,
    updated: "Today",
    lat: -17.8292,
    lng: 31.0522,
    source: "mock",
  },
  {
    id: "zm-lusaka-measles",
    country: "Zambia",
    region: "Lusaka",
    province: "Lusaka",
    disease: "Measles",
    status: "caution",
    patients: 344,
    updated: "Yesterday",
    lat: -15.3875,
    lng: 28.3228,
    source: "mock",
  },
  {
    id: "mw-lilongwe-cholera",
    country: "Malawi",
    region: "Lilongwe",
    province: "Lilongwe",
    disease: "Cholera",
    status: "critical",
    patients: 412,
    updated: "Today",
    lat: -13.9626,
    lng: 33.7741,
    source: "mock",
  },
  {
    id: "mz-maputo-dengue",
    country: "Mozambique",
    region: "Maputo",
    province: "Maputo",
    disease: "Dengue",
    status: "acceptable",
    patients: 196,
    updated: "Yesterday",
    lat: -25.9653,
    lng: 32.5892,
    source: "mock",
  },
  {
    id: "na-windhoek-measles",
    country: "Namibia",
    region: "Windhoek",
    province: "Windhoek",
    disease: "Measles",
    status: "good",
    patients: 72,
    updated: "Today",
    lat: -22.5609,
    lng: 17.0658,
    source: "mock",
  },
  {
    id: "tz-dar-es-salaam-dengue",
    country: "Tanzania",
    region: "Dar es Salaam",
    province: "Dar es Salaam",
    disease: "Dengue",
    status: "caution",
    patients: 287,
    updated: "Today",
    lat: -6.7924,
    lng: 39.2083,
    source: "mock",
  },
  {
    id: "cd-lubumbashi-malaria",
    country: "DR Congo",
    region: "Lubumbashi",
    province: "Lubumbashi",
    disease: "Malaria",
    status: "critical",
    patients: 509,
    updated: "Today",
    lat: -11.6647,
    lng: 27.4794,
    source: "mock",
  },
];

const ZAMBIA_DEMO_SIGNALS = [
  {
    id: "zm-lusaka-tb-hiv-screening",
    country: "Zambia",
    region: "Lusaka",
    province: "Lusaka",
    disease: "TB / HIV screening activity",
    status: "caution",
    patients: 1280,
    updated: "Today",
    lat: -15.3875,
    lng: 28.3228,
    source: "demo_zambia",
  },
  {
    id: "zm-copperbelt-ndola-tb-follow-up",
    country: "Zambia",
    region: "Ndola",
    province: "Copperbelt",
    disease: "TB follow-up signal",
    status: "caution",
    patients: 860,
    updated: "Today",
    lat: -12.9587,
    lng: 28.6366,
    source: "demo_zambia",
  },
  {
    id: "zm-livingstone-respiratory-alert",
    country: "Zambia",
    region: "Livingstone",
    province: "Southern Province",
    disease: "RSV / respiratory alert",
    status: "critical",
    patients: 1040,
    updated: "Today",
    lat: -17.8419,
    lng: 25.8543,
    source: "demo_zambia",
  },
  {
    id: "zw-harare-cross-border-cholera",
    country: "Zimbabwe",
    region: "Harare",
    province: "Harare",
    disease: "Cross-border cholera signal",
    status: "critical",
    patients: 894,
    updated: "Today",
    lat: -17.8292,
    lng: 31.0522,
    source: "demo_zambia",
  },
  {
    id: "za-johannesburg-respiratory-context",
    country: "South Africa",
    region: "Johannesburg",
    province: "Gauteng",
    disease: "Regional respiratory context",
    status: "acceptable",
    patients: 380,
    updated: "Today",
    lat: -26.2041,
    lng: 28.0473,
    source: "demo_zambia",
  },
];

const STATUS_COLOR_MATCH = [
  "match",
  ["get", "status"],
  "good",
  STATUS_META.good.color,
  "acceptable",
  STATUS_META.acceptable.color,
  "caution",
  STATUS_META.caution.color,
  "critical",
  STATUS_META.critical.color,
  STATUS_META.acceptable.color,
];

const DISEASE_I18N_KEYS = {
  Coronavirus: "coronavirus",
  Influenza: "influenza",
  Malaria: "malaria",
  RSV: "rsv",
  Cholera: "cholera",
  Measles: "measles",
  Dengue: "dengue",
};

function normalizeCountry(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  for (const [canonical, aliases] of Object.entries(COUNTRY_ALIASES)) {
    if (raw === canonical.toLowerCase()) return canonical;
    if (aliases.some((alias) => raw === alias || raw.includes(alias))) return canonical;
  }
  return "";
}

function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function isValidLatLng(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  return true;
}

function resolveClinicianCountry(profile = {}) {
  const candidates = [
    profile?.practice_country,
    profile?.facility_country,
    profile?.country,
    profile?.register_country,
    profile?.location_country,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeCountry(candidate);
    if (normalized) return normalized;
  }
  return "";
}

function pickPrimaryConsultancy(rows = [], selectedConsultancyId = "") {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return null;

  const selectedId = String(selectedConsultancyId || "").trim();
  if (selectedId) {
    const selected = list.find((row) => String(row?.id || "").trim() === selectedId);
    if (selected) return selected;
  }

  return (
    list.find((row) => row?.is_primary === true) ||
    [...list].sort((a, b) => {
      const orderA = Number.isFinite(Number(a?.sort_order)) ? Number(a.sort_order) : Number.MAX_SAFE_INTEGER;
      const orderB = Number.isFinite(Number(b?.sort_order)) ? Number(b.sort_order) : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      const timeA = new Date(a?.updated_at || 0).getTime();
      const timeB = new Date(b?.updated_at || 0).getTime();
      return timeB - timeA;
    })[0] ||
    null
  );
}

function resolveConsultancyLocation(row = {}) {
  return {
    country: resolveClinicianCountry(row),
    city: String(row?.facility_city || row?.city || "").trim(),
  };
}

function toFeatureCollection(signals = []) {
  return {
    type: "FeatureCollection",
    features: signals.map((signal) => {
      const severity = STATUS_META[signal.status] || STATUS_META.acceptable;
      const patientScale = Math.min(1.35, Math.log10(Math.max(10, Number(signal.patients || 0))) / 3);
      const demoBoost = /demo_zambia/i.test(String(signal.source || "")) ? 1.22 : 1;
      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [Number(signal.lng), Number(signal.lat)],
        },
        properties: {
          id: signal.id,
          status: signal.status,
          source: signal.source || "",
          country: signal.country || "",
          region: signal.region || "",
          intensity: Math.max(1.1, Math.min(3.6, severity.score * 0.72 * patientScale * demoBoost)),
        },
      };
    }),
  };
}

function rankSignal(signal) {
  const severity = STATUS_META[signal?.status] || STATUS_META.acceptable;
  return severity.score * 100000 + Number(signal?.patients || 0);
}

function chooseMockByFocus({ clinicianCountry, focusMode }) {
  if (focusMode === "country") {
    const countryRows = MOCK_SIGNALS.filter((row) => row.country === clinicianCountry);
    return countryRows.length > 0 ? countryRows : MOCK_SIGNALS;
  }
  return MOCK_SIGNALS;
}

function demoSignalsForFocus({ clinicianCountry, focusMode }) {
  return ZAMBIA_DEMO_SIGNALS;
}

function mergeSignalsWithDemo(signals = [], demoSignals = []) {
  const merged = Array.isArray(signals) ? [...signals] : [];
  const seen = new Set(merged.map((signal) => String(signal?.id || "").trim()).filter(Boolean));
  for (const signal of demoSignals) {
    const id = String(signal?.id || "").trim();
    if (!id || seen.has(id)) continue;
    merged.push(signal);
    seen.add(id);
  }
  return merged;
}

function resolveCountryLabel(t, country) {
  return resolveRegionalHealthCountryLabel(t, country);
}

function resolveDiseaseLabel(t, disease) {
  const key = DISEASE_I18N_KEYS[disease];
  if (!key) return disease || "";
  return t(`regionalHealth.diseases.${key}`, disease);
}

export default function RegionalHealth() {
  const nav = useNavigate();
  const location = useLocation();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const geo = useGeolocation();
  const profile = auth?.profile || {};
  const mapRef = React.useRef(null);
  const token = import.meta.env.VITE_MAPBOX_TOKEN;

  const forceMapResize = React.useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (typeof map.resize === "function") {
      map.resize();
      return;
    }
    map.getMap?.()?.resize?.();
  }, []);

  const [consultancyRows, setConsultancyRows] = React.useState(() =>
    readDoctorConsultancies(String(auth?.user?.id || "").trim()),
  );

  React.useEffect(() => {
    let alive = true;

    async function hydrateConsultancies() {
      const doctorUserId = String(auth?.user?.id || auth?.profile?.user_id || "").trim();
      if (!doctorUserId) return;

      const localRows = readDoctorConsultancies(doctorUserId);
      if (alive && Array.isArray(localRows) && localRows.length > 0) {
        setConsultancyRows(localRows);
      }

      try {
        const { data, error } = await supabase
          .from("doctor_consultancies")
          .select(
            "id,facility_name,facility_city,facility_country,is_primary,sort_order,updated_at",
          )
          .eq("doctor_id", doctorUserId)
          .order("sort_order", { ascending: true })
          .order("updated_at", { ascending: false });

        if (error || !alive || !Array.isArray(data)) return;
        setConsultancyRows(data);
      } catch (err) {
        console.error("Regional health consultancy hydrate failed", err);
      }
    }

    hydrateConsultancies();
    return () => {
      alive = false;
    };
  }, [auth?.profile?.user_id, auth?.user?.id]);

  const selectedConsultancy = React.useMemo(
    () => pickPrimaryConsultancy(consultancyRows, location.state?.consultancyId),
    [consultancyRows, location.state?.consultancyId],
  );
  const selectedConsultancyLocation = React.useMemo(
    () => resolveConsultancyLocation(selectedConsultancy),
    [selectedConsultancy],
  );
  const clinicianCountry = React.useMemo(() => {
    if (selectedConsultancyLocation.country) return selectedConsultancyLocation.country;
    return resolveClinicianCountry(profile);
  }, [profile, selectedConsultancyLocation.country]);
  const hasConsultancyLocation = Boolean(
    selectedConsultancyLocation.country || selectedConsultancyLocation.city,
  );
  const countryView = COUNTRY_VIEW[clinicianCountry] || null;

  const [focusMode, setFocusMode] = React.useState(() =>
    clinicianCountry ? "country" : "sadc",
  );
  const focusTouchedRef = React.useRef(false);
  const [signals, setSignals] = React.useState(() =>
    mergeSignalsWithDemo(
      chooseMockByFocus({ clinicianCountry, focusMode: "country" }),
      demoSignalsForFocus({ clinicianCountry, focusMode: "country" })
    )
  );
  const [signalsLoading, setSignalsLoading] = React.useState(false);
  const [signalsSource, setSignalsSource] = React.useState("mock");
  const [selectedSignalId, setSelectedSignalId] = React.useState("");
  const [mapReady, setMapReady] = React.useState(false);

  const showSouthAfricaChoropleth =
    focusMode === "country" && clinicianCountry === "South Africa";

  const interactiveLayerIds = React.useMemo(() => {
    if (showSouthAfricaChoropleth) return ["regional-health-core", "sa-province-fill"];
    return ["regional-health-core"];
  }, [showSouthAfricaChoropleth]);

  const handleBack = React.useCallback(() => {
    try {
      if (window.history.length > 1) nav(-1);
      else nav("/home/doctor");
    } catch {
      nav("/home/doctor");
    }
  }, [nav]);

  React.useEffect(() => {
    if (!clinicianCountry && focusMode === "country") {
      setFocusMode("sadc");
    }
  }, [clinicianCountry, focusMode]);

  React.useEffect(() => {
    if (clinicianCountry && !focusTouchedRef.current) {
      setFocusMode("country");
    }
  }, [clinicianCountry]);

  React.useEffect(() => {
    let alive = true;
    async function loadSignals() {
      setSignalsLoading(true);
      const requestedCountry = focusMode === "country" ? clinicianCountry : "";
      const { data, error } = await regionalHealthService.getSignals({
        country: requestedCountry,
        limit: 800,
      });

      const fallback = chooseMockByFocus({ clinicianCountry, focusMode });
      const baseSignals = Array.isArray(data) && data.length > 0 ? data : fallback;
      const nextSignals = mergeSignalsWithDemo(
        baseSignals,
        demoSignalsForFocus({ clinicianCountry, focusMode })
      );
      const nextSource = !error && Array.isArray(data) && data.length > 0 ? "db" : "mock";

      if (!alive) return;
      setSignals(nextSignals);
      setSignalsSource(nextSource);
      setSignalsLoading(false);
    }

    loadSignals();
    return () => {
      alive = false;
    };
  }, [clinicianCountry, focusMode]);

  React.useEffect(() => {
    if (!selectedSignalId) return;
    const stillVisible = signals.some((item) => item.id === selectedSignalId);
    if (!stillVisible) {
      setSelectedSignalId("");
    }
  }, [selectedSignalId, signals]);

  const featuredSignal =
    [...signals].sort((a, b) => rankSignal(b) - rankSignal(a))[0] || null;
  const selectedSignal =
    signals.find((item) => item.id === selectedSignalId) || null;

  const geojson = React.useMemo(() => toFeatureCollection(signals), [signals]);

  React.useEffect(() => {
    const requiredIds = new Set([
      "zm-lusaka-tb-hiv-screening",
      "zm-copperbelt-ndola-tb-follow-up",
      "zm-livingstone-respiratory-alert",
      "zw-harare-cross-border-cholera",
    ]);
    const requiredSignals = signals
      .filter((signal) => requiredIds.has(signal.id))
      .map((signal) => ({
        id: signal.id,
        status: signal.status,
        lat: Number(signal.lat),
        lng: Number(signal.lng),
        source: signal.source,
      }));
    const statusCounts = signals.reduce((acc, signal) => {
      const key = signal.status || "unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    console.info("[regional-health] rendered signals", {
      count: signals.length,
      statusCounts,
      requiredSignals,
      featureCount: geojson.features.length,
    });
  }, [geojson.features.length, signals]);

  React.useEffect(() => {
    if (!mapReady) return;
    const timer = window.setTimeout(() => {
      const map = mapRef.current?.getMap?.() || mapRef.current;
      if (!map) return;
      const source = map.getSource?.("regional-health-signals");
      const layerIds = [
        "regional-health-heat",
        "regional-health-demo-halo",
        "regional-health-core",
      ];
      const layers = layerIds.map((id) => ({
        id,
        present: Boolean(map.getLayer?.(id)),
      }));
      const renderedFeatureCount =
        map.queryRenderedFeatures?.({ layers: layerIds.filter((id) => map.getLayer?.(id)) })
          ?.length || 0;
      console.info("[regional-health] map overlay state", {
        sourcePresent: Boolean(source),
        layers,
        renderedFeatureCount,
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [geojson, mapReady]);

  const southAfricaProvinceGeojson = React.useMemo(() => {
    const saSignals = signals.filter(
      (item) => normalizeCountry(item.country) === "South Africa"
    );
    return buildSouthAfricaProvinceChoropleth(saSignals);
  }, [signals]);

  const initialViewState = React.useMemo(() => {
    const userLat = Number(geo?.coords?.lat);
    const userLng = Number(geo?.coords?.lng);
    const canUseUserLocation =
      isValidLatLng(userLat, userLng) &&
      geo?.consent !== "denied" &&
      (geo?.status === "granted" || geo?.consent === "always");

    if (canUseUserLocation) {
      return {
        latitude: userLat,
        longitude: userLng,
        zoom: 7.2,
      };
    }

    if (countryView) {
      return {
        latitude: countryView.center[1],
        longitude: countryView.center[0],
        zoom: countryView.zoom,
      };
    }
    return SADC_DEFAULT_VIEW;
  }, [countryView, geo?.consent, geo?.coords?.lat, geo?.coords?.lng, geo?.status]);

  const fitFocus = React.useCallback((opts = {}) => {
    const { preferUserLocation = false } = opts;
    const map = mapRef.current;
    if (!map) return;

    if (preferUserLocation) {
      const userLat = Number(geo?.coords?.lat);
      const userLng = Number(geo?.coords?.lng);
      const canUseUserLocation =
        isValidLatLng(userLat, userLng) &&
        geo?.consent !== "denied" &&
        (geo?.status === "granted" || geo?.consent === "always");
      if (canUseUserLocation) {
        map.flyTo({
          center: [userLng, userLat],
          zoom: 7.2,
          duration: 900,
        });
        return;
      }
    }

    if (focusMode === "country" && countryView?.bounds) {
      map.fitBounds(countryView.bounds, {
        padding: { top: 24, right: 24, bottom: 24, left: 24 },
        duration: 900,
      });
      return;
    }
    map.fitBounds(SADC_BOUNDS, {
      padding: { top: 18, right: 18, bottom: 18, left: 18 },
      duration: 900,
    });
  }, [
    countryView,
    focusMode,
    geo?.consent,
    geo?.coords?.lat,
    geo?.coords?.lng,
    geo?.status,
  ]);

  React.useEffect(() => {
    fitFocus({ preferUserLocation: true });
  }, [fitFocus]);

  React.useEffect(() => {
    const raf = requestAnimationFrame(() => forceMapResize());
    const timeout = setTimeout(() => forceMapResize(), 240);
    const onResize = () => forceMapResize();
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("resize", onResize);
    };
  }, [forceMapResize]);

  const onMapClick = React.useCallback(
    (event) => {
      const features = Array.isArray(event?.features) ? event.features : [];

      const pickedSignal = features.find(
        (feature) =>
          feature?.properties?.id && String(feature?.layer?.id || "") === "regional-health-core"
      );
      if (pickedSignal?.properties?.id) {
        setSelectedSignalId(String(pickedSignal.properties.id));
        return;
      }

      if (!showSouthAfricaChoropleth) return;

      const pickedProvince = features.find(
        (feature) =>
          feature?.properties?.province &&
          String(feature?.layer?.id || "") === "sa-province-fill"
      );
      const province = String(pickedProvince?.properties?.province || "").trim();
      if (!province) return;

      const provinceKey = normalizeText(province);
      const match = [...signals]
        .filter((item) => normalizeCountry(item.country) === "South Africa")
        .filter((item) => {
          const itemRegion = normalizeText(item.province || item.region);
          return (
            itemRegion === provinceKey ||
            itemRegion.includes(provinceKey) ||
            provinceKey.includes(itemRegion)
          );
        })
        .sort((a, b) => rankSignal(b) - rankSignal(a))[0];

      if (match?.id) {
        setSelectedSignalId(match.id);
      }
    },
    [showSouthAfricaChoropleth, signals]
  );

  const isSampleFeed = React.useMemo(
    () =>
      signalsSource !== "db" ||
      signals.some((signal) =>
        /mock|demo|seed/i.test(String(signal?.source || ""))
      ),
    [signals, signalsSource]
  );

  const sourceLabel =
    !isSampleFeed
      ? t("regionalHealth.source.live", "Live regional feed")
      : !hasConsultancyLocation
      ? t(
          "regionalHealth.source.demoNeedsLocation",
          "Sample regional feed. Add a consultancy location to default the map to your region.",
        )
      : t("regionalHealth.source.demo", "Sample regional feed");

  const localizedStatusMeta = React.useMemo(
    () =>
      Object.entries(STATUS_META).map(([key, meta]) => ({
        key,
        ...meta,
        label: t(`regionalHealth.status.${key}`, meta.label),
      })),
    [t]
  );

  const saChoroplethFillLayer = React.useMemo(
    () => ({
      id: "sa-province-fill",
      type: "fill",
      paint: {
        "fill-color": isDark ? "#6F86B8" : "#DCE6FA",
        "fill-opacity": isDark ? 0.08 : 0.18,
      },
    }),
    [isDark]
  );

  const saChoroplethLineLayer = React.useMemo(
    () => ({
      id: "sa-province-line",
      type: "line",
      paint: {
        "line-color": isDark ? "rgba(178,194,228,0.18)" : "rgba(148,165,214,0.22)",
        "line-width": isDark ? 0.55 : 0.45,
      },
    }),
    [isDark]
  );

  const heatLayer = React.useMemo(
    () => ({
      id: "regional-health-heat",
      type: "circle",
      paint: {
        "circle-color": STATUS_COLOR_MATCH,
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          ["*", isDark ? 17 : 23, ["get", "intensity"]],
          5,
          ["*", isDark ? 29 : 40, ["get", "intensity"]],
          8,
          ["*", isDark ? 49 : 64, ["get", "intensity"]],
          11,
          ["*", isDark ? 70 : 92, ["get", "intensity"]],
        ],
        "circle-opacity": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          isDark ? 0.16 : 0.27,
          6,
          isDark ? 0.21 : 0.34,
          10,
          isDark ? 0.27 : 0.42,
        ],
        "circle-blur": isDark ? 0.92 : 1.18,
      },
    }),
    [isDark]
  );

  const demoHaloLayer = React.useMemo(
    () => ({
      id: "regional-health-demo-halo",
      type: "circle",
      filter: ["==", ["get", "source"], "demo_zambia"],
      paint: {
        "circle-color": STATUS_COLOR_MATCH,
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          ["*", isDark ? 14 : 19, ["get", "intensity"]],
          5,
          ["*", isDark ? 24 : 33, ["get", "intensity"]],
          8,
          ["*", isDark ? 38 : 52, ["get", "intensity"]],
          11,
          ["*", isDark ? 56 : 72, ["get", "intensity"]],
        ],
        "circle-opacity": isDark ? 0.24 : 0.3,
        "circle-blur": 0.72,
        "circle-stroke-color": [
          "case",
          ["==", ["get", "status"], "critical"],
          "rgba(247,84,58,0.52)",
          ["==", ["get", "status"], "caution"],
          "rgba(240,177,0,0.52)",
          "rgba(142,108,239,0.46)",
        ],
        "circle-stroke-width": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          1,
          8,
          2,
          11,
          3,
        ],
        "circle-stroke-opacity": isDark ? 0.24 : 0.3,
      },
    }),
    [isDark]
  );

  const coreLayer = React.useMemo(
    () => ({
      id: "regional-health-core",
      type: "circle",
      paint: {
        "circle-color": STATUS_COLOR_MATCH,
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          3,
          3.5,
          7,
          6,
          11,
          8.5,
        ],
        "circle-stroke-color": isDark
          ? "rgba(245,247,255,0.18)"
          : "rgba(255,255,255,0.28)",
        "circle-stroke-width": 0.6,
        "circle-opacity": isDark ? 0.9 : 0.84,
      },
    }),
    [isDark]
  );

  const selectedSignalStatusLabel = t(
    `regionalHealth.status.${selectedSignal?.status}`,
    STATUS_META[selectedSignal?.status]?.label || ""
  );
  const selectedSignalUpdatedLabel = t(
    `regionalHealth.updated.${String((selectedSignal || featuredSignal)?.updated || "").toLowerCase()}`,
    (selectedSignal || featuredSignal)?.updated || t("regionalHealth.updated.today", "Today")
  );
  const clinicianCountryLabel = clinicianCountry
    ? resolveCountryLabel(t, clinicianCountry)
    : t("regionalHealth.focus.savedLocation", "Saved location");

  return (
      <div className="w-full" data-hide-tabbar="true">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4">
        <header className="mb-0">
          <TopBarCenter
            title={t("regionalHealth.title", "Regional health")}
            onBack={handleBack}
          />
        </header>

        <SettingsCard fullWidth className="mb-3 p-0">
          <div className="relative h-[320px] w-full min-w-0 overflow-hidden rounded-[12px] md:h-[420px]">
              {token ? (
                <MapGL
                  ref={mapRef}
                  mapboxAccessToken={token}
                  initialViewState={initialViewState}
                  mapStyle={getAfriMedMapStyle(isDark)}
                  interactiveLayerIds={interactiveLayerIds}
                  onLoad={() => {
                    setMapReady(true);
                    forceMapResize();
                    if (typeof window !== "undefined") {
                      window.__AFRIMED_REGIONAL_HEALTH_MAP__ =
                        mapRef.current?.getMap?.() || mapRef.current || null;
                    }
                  }}
                  onClick={onMapClick}
                  dragRotate={false}
                  touchPitch={false}
                  attributionControl={false}
                  style={{ position: "absolute", inset: 0 }}
                >
                  {showSouthAfricaChoropleth ? (
                    <Source
                      id="regional-health-sa-provinces"
                      type="geojson"
                      data={southAfricaProvinceGeojson}
                    >
                      <Layer {...saChoroplethFillLayer} />
                      <Layer {...saChoroplethLineLayer} />
                    </Source>
                  ) : null}

                  <Source id="regional-health-signals" type="geojson" data={geojson}>
                    <Layer {...heatLayer} />
                    <Layer {...demoHaloLayer} />
                    <Layer {...coreLayer} />
                  </Source>

                  <NavigationControl position="bottom-right" showCompass={false} />

                  {selectedSignal ? (
                    <Popup
                      latitude={Number(selectedSignal.lat)}
                      longitude={Number(selectedSignal.lng)}
                      closeOnClick={false}
                      closeButton={false}
                      onClose={() => setSelectedSignalId("")}
                      offset={20}
                      maxWidth="220px"
                    >
                      <div className="relative min-w-[180px] pr-10">
                        <button
                          type="button"
                          aria-label={t("common.close", "Close")}
                          onClick={() => setSelectedSignalId("")}
                          className="absolute right-1 top-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(255,255,255,0.96)] text-[rgb(var(--clr-on-surface-variant))] shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors hover:bg-[rgba(243,244,247,0.98)]"
                        >
                          <span aria-hidden="true" className="text-[22px] leading-none">
                            ×
                          </span>
                        </button>
                        <div className="text-label-medium text-on-surface">
                          {selectedSignal.region}
                        </div>
                        <div className="text-body-small text-on-surface-var">
                          {resolveCountryLabel(t, selectedSignal.country)}
                        </div>
                        <div className="mt-1 text-body-small text-on-surface-var">
                          {selectedSignalStatusLabel} ·{" "}
                          {t("regionalHealth.metrics.patients", "{count} patients", {
                            count: selectedSignal.patients,
                          })}
                        </div>
                      </div>
                    </Popup>
                  ) : null}
                </MapGL>
              ) : (
                <div className="h-full w-full grid place-items-center bg-[#DDE3F2] text-body-small text-on-surface-var">
                  {t("regionalHealth.errors.mapTokenMissing", "Map token missing")}
                </div>
              )}
          </div>
        </SettingsCard>

        <section className="mb-4">
          <RegionalHealthMapScopeControls
            activeScope={focusMode}
            countryLabel={clinicianCountryLabel}
            countryEnabled={Boolean(clinicianCountry)}
            resetLabel={t("regionalHealth.actions.resetView", "Reset view")}
            onCountry={() => {
              if (!clinicianCountry) return;
              focusTouchedRef.current = true;
              setFocusMode("country");
            }}
            onSadc={() => {
              focusTouchedRef.current = true;
              setFocusMode("sadc");
            }}
            onReset={() => fitFocus({ preferUserLocation: false })}
          />

          <div className="mb-2 px-1 text-body-small text-on-surface-var">
            {signalsLoading
              ? t("regionalHealth.loading", "Loading regional feed...")
              : sourceLabel}
          </div>

          <SettingsCard fullWidth className="py-2">
            <RegionalHealthMapLegendRows items={localizedStatusMeta} />
          </SettingsCard>
        </section>

        <section className="pb-[calc(128px+env(safe-area-inset-bottom))] md:pb-10">
          <div className="px-4 pb-2 text-body-medium text-on-surface-var">
            {isSampleFeed
              ? t("regionalHealth.sampleAlert", "Sample alert")
              : t("regionalHealth.newAlert", "New Alert")}
          </div>
          <SettingsCard fullWidth className="p-0">
            <button
              type="button"
              onClick={() => {
                if ((selectedSignal || featuredSignal)?.id) {
                  setSelectedSignalId((selectedSignal || featuredSignal).id);
                }
              }}
              className="w-full px-4 py-3 text-left"
            >
              <div className="flex items-start gap-3">
                <img
                  src={ICON_CORONAVIRUS}
                  alt=""
                  className="mt-0.5 h-6 w-6"
                  draggable="false"
                />

                <div className="min-w-0">
                  <div className="text-body-large text-on-surface">
                    {resolveDiseaseLabel(
                      t,
                      (selectedSignal || featuredSignal)?.disease
                    ) ||
                      t("regionalHealth.diseaseFallback", "Coronavirus")}
                  </div>
                  <div className="text-body-medium text-on-surface-var">
                    {t("regionalHealth.metrics.patientsTitle", "{count} Patients", {
                      count: (selectedSignal || featuredSignal)?.patients || 0,
                    })}
                  </div>
                  <div className="text-body-medium text-on-surface-var">
                    {t("regionalHealth.metrics.update", "Update")} • {selectedSignalUpdatedLabel}
                  </div>
                </div>
              </div>
            </button>
          </SettingsCard>
        </section>
        </div>
      </div>
  );
}
